import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConfig } from '../../hooks/useAI';
import { apiUrl } from '../../lib/api-url';

export default function AIBusinessPage() {
  const queryClient = useQueryClient();
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});

  const { data: config } = useQuery({ queryKey: ['ai-config'], queryFn: getConfig });

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['ai-business-profile'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/business/profile'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      return json.data;
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async (url: string) => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/business/analyze'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ website_url: url }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || json.error);
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-business-profile'] });
      setAnalyzing(false);
    },
    onError: () => setAnalyzing(false),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/business/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || json.error);
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-business-profile'] });
      setEditMode(false);
    },
  });

  const handleAnalyze = () => {
    if (!websiteUrl.trim()) return;
    setAnalyzing(true);
    analyzeMutation.mutate(websiteUrl);
  };

  const handleSaveEdit = () => {
    updateMutation.mutate(editData);
  };

  const profile = profileData?.profile;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">AI Business Understanding</h2>
        <p className="text-gray-500 mt-1">Let AI learn about your business to personalize all outputs.</p>
      </div>

      {/* Website Analysis */}
      {!profile && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyze Your Website</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Enter your website URL and AI will extract your business type, tone, products, and more to personalize all AI outputs.
          </p>
          <div className="flex gap-3 max-w-md mx-auto">
            <input
              type="text"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="https://yourwebsite.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
            <button
              onClick={handleAnalyze}
              disabled={analyzing || !websiteUrl.trim()}
              className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {analyzing ? 'Analyzing...' : 'Analyze'}
            </button>
          </div>
        </div>
      )}

      {/* Business Profile */}
      {profile && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Business Profile</h3>
              <p className="text-sm text-gray-500">AI-learned information about your business</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setEditMode(!editMode); setEditData({}); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {editMode ? 'Cancel' : 'Edit'}
              </button>
              <button
                onClick={() => { setWebsiteUrl(profile.website_url || ''); setAnalyzing(true); analyzeMutation.mutate(profile.website_url); }}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
              >
                Re-analyze
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Company Name</label>
                {editMode ? (
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1"
                    value={editData.company_name ?? profile.company_name ?? ''}
                    onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900 mt-1">{profile.company_name || '—'}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Business Type</label>
                {editMode ? (
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1"
                    value={editData.business_type ?? profile.business_type ?? ''}
                    onChange={(e) => setEditData({ ...editData, business_type: e.target.value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900 mt-1 capitalize">{profile.business_type || '—'}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Industry</label>
                {editMode ? (
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1"
                    value={editData.industry ?? profile.industry ?? ''}
                    onChange={(e) => setEditData({ ...editData, industry: e.target.value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900 mt-1 capitalize">{profile.industry || '—'}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Tone</label>
                {editMode ? (
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1"
                    value={editData.tone ?? profile.tone ?? ''}
                    onChange={(e) => setEditData({ ...editData, tone: e.target.value })}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900 mt-1 capitalize">{profile.tone || '—'}</p>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 p-6">
              <label className="text-xs font-medium text-gray-500 uppercase">Target Audience</label>
              {editMode ? (
                <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1" rows={2}
                  value={editData.target_audience ?? profile.target_audience ?? ''}
                  onChange={(e) => setEditData({ ...editData, target_audience: e.target.value })}
                />
              ) : (
                <p className="text-sm text-gray-700 mt-1">{profile.target_audience || '—'}</p>
              )}
            </div>

            {profile.products_services && profile.products_services.length > 0 && (
              <div className="border-t border-gray-200 p-6">
                <label className="text-xs font-medium text-gray-500 uppercase">Products & Services</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {profile.products_services.slice(0, 10).map((item: string, i: number) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">{item}</span>
                  ))}
                </div>
              </div>
            )}

            {profile.key_selling_points && profile.key_selling_points.length > 0 && (
              <div className="border-t border-gray-200 p-6">
                <label className="text-xs font-medium text-gray-500 uppercase">Key Selling Points</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {profile.key_selling_points.slice(0, 5).map((item: string, i: number) => (
                    <span key={i} className="px-3 py-1 bg-green-50 text-green-700 text-xs rounded-full">{item}</span>
                  ))}
                </div>
              </div>
            )}

            {profile.website_summary && (
              <div className="border-t border-gray-200 p-6">
                <label className="text-xs font-medium text-gray-500 uppercase">Website Summary</label>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{profile.website_summary}</p>
              </div>
            )}
          </div>

          {editMode && (
            <button
              onClick={handleSaveEdit}
              disabled={updateMutation.isPending}
              className="mt-4 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
